import type {
  OperationalRecordingDetail,
  OperationalRecordingsPage,
  OperationalUploadCreate,
  OperationalUploadParticipants,
  OperationalUploadSession,
  RecordingCapabilitiesV1,
  SignalOverviewV1,
  SignalWindowV1,
} from "@rekonas/domain";
import { PORTABLE_RECORDING_REF_PATTERN } from "@rekonas/domain";
import { authCacheScope } from "./cacheScope";
import { authorizedFetch, parseResponse } from "./api";
import {
  parseRecordingCapabilities,
  parseSignalOverview,
  parseSignalProfile,
  parseSignalWindow,
} from "./sponsorData";

type JsonObject = Record<string, unknown>;

function object(value: unknown, context: string): JsonObject {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`Invalid ${context}`);
  }
  return value as JsonObject;
}

function string(value: unknown, context: string): string {
  if (typeof value !== "string" || !value) throw new TypeError(`Invalid ${context}`);
  return value;
}

function exactKeys(value: JsonObject, keys: string[], context: string): void {
  const expected = new Set(keys);
  if (Object.keys(value).some((key) => !expected.has(key)) || keys.some((key) => !(key in value))) {
    throw new TypeError(`Invalid ${context} fields`);
  }
}

export function parseOperationalRecording(value: unknown): OperationalRecordingDetail {
  const item = object(value, "operational recording");
  const recordingRef = string(item.recording_ref, "operational recording_ref");
  if (!PORTABLE_RECORDING_REF_PATTERN.test(recordingRef)) throw new TypeError("Invalid operational recording_ref");
  for (const forbidden of ["recording", "job_id", "release_id", "bundle", "prefix", "bucket", "storage_url"]) {
    if (forbidden in item) throw new TypeError(`Invalid operational recording.${forbidden}`);
  }
  const states = ["queued", "converting", "processing", "available", "failed"];
  for (const field of ["signals", "hypnogram", "viewer"] as const) {
    if (!states.includes(String(item[field]))) throw new TypeError(`Invalid operational recording.${field}`);
  }
  const productStates = ["processing", "signals_available", "attention_required", "ready", "failed"];
  if (!productStates.includes(String(item.state))) throw new TypeError("Invalid operational recording.state");
  if (item.duration_seconds !== null && item.duration_seconds !== undefined &&
      (typeof item.duration_seconds !== "number" || !Number.isFinite(item.duration_seconds) || item.duration_seconds <= 0)) {
    throw new TypeError("Invalid operational recording.duration_seconds");
  }
  if (!Array.isArray(item.device_summary) || item.device_summary.some((entry) => typeof entry !== "string")) {
    throw new TypeError("Invalid operational recording.device_summary");
  }
  if (item.profile !== undefined) parseSignalProfile(item.profile);
  return item as unknown as OperationalRecordingDetail;
}

export function parseOperationalRecordingsPage(value: unknown): OperationalRecordingsPage {
  const page = object(value, "operational recordings page");
  if (!Array.isArray(page.items) || page.items.length > 100) throw new TypeError("Invalid operational recordings items");
  const items = page.items.map(parseOperationalRecording);
  const next = page.next_cursor;
  if (next !== null && (typeof next !== "string" || !PORTABLE_RECORDING_REF_PATTERN.test(next))) {
    throw new TypeError("Invalid operational recordings cursor");
  }
  return { items, next_cursor: next as string | null };
}

function route(workspaceRef: string, suffix = ""): string {
  return `/platform/v1/workspaces/${encodeURIComponent(workspaceRef)}/recordings${suffix}`;
}

export const operationalKeys = {
  all: () => ["operational-recordings", authCacheScope()] as const,
  list: (workspaceRef: string) => ["operational-recordings", authCacheScope(), workspaceRef, "list"] as const,
  uploadParticipants: (workspaceRef: string) => ["operational-recordings", authCacheScope(), workspaceRef, "upload-participants"] as const,
  detail: (workspaceRef: string, recordingRef: string) =>
    ["operational-recordings", authCacheScope(), workspaceRef, recordingRef, "detail"] as const,
  signalScope: (workspaceRef: string, recordingRef: string, dataVersion: string) =>
    ["operational-signals", authCacheScope(), workspaceRef, recordingRef, dataVersion] as const,
};

export async function listOperationalRecordings(
  workspaceRef: string,
  signal?: AbortSignal,
): Promise<OperationalRecordingsPage> {
  const response = await parseResponse<unknown>(await authorizedFetch(route(workspaceRef), { signal }));
  return parseOperationalRecordingsPage(response);
}

export function parseOperationalUploadParticipants(value: unknown): OperationalUploadParticipants {
  const document = object(value, "operational upload participants");
  exactKeys(document, ["participant_refs"], "operational upload participants");
  if (!Array.isArray(document.participant_refs) || document.participant_refs.length > 100 ||
      document.participant_refs.some((ref) => typeof ref !== "string" ||
        !/^part_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(ref))) {
    throw new TypeError("Invalid operational upload participant refs");
  }
  return { participant_refs: [...new Set(document.participant_refs)].sort() };
}

export async function listOperationalUploadParticipants(
  workspaceRef: string,
  signal?: AbortSignal,
): Promise<OperationalUploadParticipants> {
  const response = await parseResponse<unknown>(await authorizedFetch(
    `/platform/v1/workspaces/${encodeURIComponent(workspaceRef)}/upload-participants`,
    { signal },
  ));
  return parseOperationalUploadParticipants(response);
}

export async function getOperationalRecording(
  workspaceRef: string,
  recordingRef: string,
  signal?: AbortSignal,
): Promise<OperationalRecordingDetail> {
  const response = await parseResponse<unknown>(await authorizedFetch(
    route(workspaceRef, `/${encodeURIComponent(recordingRef)}`), { signal },
  ));
  return parseOperationalRecording(response);
}

type PinnedRequest = {
  workspaceRef: string;
  recordingRef: string;
  dataVersion: string;
  profileSha256: string;
  signal?: AbortSignal;
};

function scientificRoute(request: PinnedRequest, operation: string, query: URLSearchParams): string {
  query.set("data_version", request.dataVersion);
  query.set("profile_sha256", request.profileSha256);
  return route(request.workspaceRef, `/${encodeURIComponent(request.recordingRef)}/${operation}?${query}`);
}

export async function fetchOperationalCapabilities(request: PinnedRequest): Promise<RecordingCapabilitiesV1> {
  const response = await parseResponse<unknown>(await authorizedFetch(
    scientificRoute(request, "capabilities", new URLSearchParams()), { signal: request.signal },
  ));
  return parseRecordingCapabilities(response);
}

export async function fetchOperationalOverview(
  request: PinnedRequest & { modalities: string[]; maxPoints: number },
): Promise<SignalOverviewV1> {
  const query = new URLSearchParams({ max_points: String(request.maxPoints) });
  if (request.modalities.length) query.set("modalities", request.modalities.join(","));
  const response = await parseResponse<unknown>(await authorizedFetch(
    scientificRoute(request, "overview", query), { signal: request.signal },
  ));
  return parseSignalOverview(response);
}

export async function fetchOperationalWindow(
  request: PinnedRequest & {
    modalities: string[];
    startSeconds: number;
    durationSeconds: 15 | 30 | 60;
    maxPoints?: number;
  },
): Promise<SignalWindowV1> {
  const query = new URLSearchParams({
    start_seconds: String(request.startSeconds),
    duration_seconds: String(request.durationSeconds),
    max_points: String(request.maxPoints ?? 200_000),
  });
  if (request.modalities.length) query.set("modalities", request.modalities.join(","));
  const response = await parseResponse<unknown>(await authorizedFetch(
    scientificRoute(request, "window", query), { signal: request.signal },
  ));
  return parseSignalWindow(response);
}

const UPLOAD_REF = /^upl_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const UPLOAD_STATES = ["created", "uploading", "finalizing", "accepted", "rejected", "conflict", "expired"];

export function parseOperationalUploadSession(value: unknown): OperationalUploadSession {
  const session = object(value, "operational upload session");
  exactKeys(session, ["upload_ref", "state", "recording_ref", "error", "tus"], "operational upload session");
  const uploadRef = string(session.upload_ref, "operational upload_ref");
  if (!UPLOAD_REF.test(uploadRef) || !UPLOAD_STATES.includes(String(session.state))) {
    throw new TypeError("Invalid operational upload identity or state");
  }
  if (session.recording_ref !== null &&
      (typeof session.recording_ref !== "string" || !PORTABLE_RECORDING_REF_PATTERN.test(session.recording_ref))) {
    throw new TypeError("Invalid operational upload recording_ref");
  }
  if (session.error !== null) {
    const error = object(session.error, "operational upload error");
    exactKeys(error, ["code"], "operational upload error");
    string(error.code, "operational upload error code");
  }
  const tus = object(session.tus, "operational upload tus");
  exactKeys(tus, ["endpoint", "upload_url", "metadata", "size_bytes"], "operational upload tus");
  if (typeof tus.endpoint !== "string" || !tus.endpoint.startsWith("/uploads/") ||
      (tus.upload_url !== null && (typeof tus.upload_url !== "string" || !tus.upload_url.startsWith("/uploads/"))) ||
      !Number.isInteger(tus.size_bytes) || Number(tus.size_bytes) <= 0) {
    throw new TypeError("Invalid operational tus transport");
  }
  const metadata = object(tus.metadata, "operational upload tus metadata");
  exactKeys(metadata, ["upload_ref", "filename", "filetype"], "operational upload tus metadata");
  if (metadata.upload_ref !== uploadRef || typeof metadata.filename !== "string" || metadata.filetype !== "application/gzip") {
    throw new TypeError("Invalid operational tus metadata");
  }
  return session as unknown as OperationalUploadSession;
}

export async function createOperationalUploadSession(
  request: OperationalUploadCreate,
  signal?: AbortSignal,
): Promise<OperationalUploadSession> {
  return parseOperationalUploadSession(await parseResponse<unknown>(await authorizedFetch(
    "/platform/mobile/v1/uploads",
    { method: "POST", body: JSON.stringify(request), signal },
  )));
}

export async function getOperationalUploadSession(
  uploadRef: string,
  signal?: AbortSignal,
): Promise<OperationalUploadSession> {
  if (!UPLOAD_REF.test(uploadRef)) throw new TypeError("Invalid operational upload_ref");
  return parseOperationalUploadSession(await parseResponse<unknown>(await authorizedFetch(
    `/platform/mobile/v1/uploads/${encodeURIComponent(uploadRef)}`,
    { signal },
  )));
}
